import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { dayjs, formatDate, toDateString } from '@koob/dates';
import { Formik, Form, getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import { KoobCard, KoobModal, KoobButton, KoobInputNumber } from '@koob/ui';
import KoobTitle from '../../../../../components/Koob/KoobTitle';
import KoobParagraph from '../../../../../components/Koob/KoobParagraph';
import { useApp, useSnack } from '../../../../../hooks';
import AllotmentsEditorMonthSelector, {
  AllotmentButton,
} from '../../../../Allotments/Partials/AllotmentsEditorMonthSelector';
import NightRatesOrganizationSelector, {
  useNightRatesOrganizations,
} from './partials/NightRatesOrganizationSelector';
import NightRatesTargetOrganizationsModal from './partials/NightRatesTargetOrganizationsModal';
import FormNavigationBlocker from '../../../../../components/Fields/FormNavigationBlocker';
import NightRatesRoomSelector from './partials/NightRatesRoomSelector';
import NightStatusEnum from 'constants/enums/NightStatusEnum';
import { nightStatusOf } from './seasons/nightStatus';
import { useAsync } from 'react-async';
import { getPromotions } from 'api/node/promotions';
import { getHotelNightRates, updateHotelNightRates } from 'api/node/nightRates';
import { ProductTypeEnum, decodeId } from 'utils';
import { PromotionTypeEnum } from '@koob/enums';

// Click-to-edit value used in the night-rates grid: shows the value as plain
// text and only turns into an input when clicked — same interaction as the
// legacy click-to-edit price fields. Clicking the value stops
// propagation so it doesn't also toggle the night selection on the cell.
// Defined at module scope so its editing state survives parent re-renders.
function EditableCellValue({
  name,
  value,
  onValueChange,
  min,
  integer,
  displayClassName,
}) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <div className="w-20">
        <KoobInputNumber
          name={name}
          size="sm"
          min={min}
          integer={integer}
          value={value}
          autoFocus
          onValueChange={(next) => onValueChange(name, next ?? '')}
          onClick={(event) => event.stopPropagation()}
          onBlur={() => setIsEditing(false)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setIsEditing(false);
            }
          }}
        />
      </div>
    );
  }

  const isEmpty = value === '' || value === null || value === undefined;

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        setIsEditing(true);
      }}
      className={displayClassName}
    >
      {isEmpty ? '—' : value}
    </button>
  );
}

// Benefit kinds shown in the night-rates "Supplements" row: both the fixed
// amount supplement and the percentage one.
const SUPPLEMENT_BENEFIT_KINDS = [
  'Benefits::Supplement',
  'Benefits::SupplementPercent',
];

// Their promotion-type slugs, sent as a comma separated list so the listing
// endpoint returns both kinds in a single call.
const SUPPLEMENT_PROMOTION_TYPES = [
  PromotionTypeEnum.SUPPLEMENT,
  PromotionTypeEnum.SUPPLEMENT_PERCENT,
].join(',');

// Weekday names in ISO order (Monday first), matching the day names the promotion
// `applicablePeriods[].days` are serialized to server-side.
const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

function promotionNightsCondition(promo) {
  return Number(promo.numberOfNights ?? 0);
}

// A night's effective minimum stay: the value currently in the grid (so an
// unsaved edit immediately re-filters the supplements below it), falling back to
// the period default and finally to 1 when the cell has been emptied.
function resolveMinStay(cellValue, periodDefault) {
  for (const candidate of [cellValue, periodDefault]) {
    const parsed = Number(candidate);
    if (candidate !== '' && candidate != null && Number.isFinite(parsed)) {
      return Math.max(parsed, 1);
    }
  }
  return 1;
}

// A period without a default (group price not set) has nothing to inherit:
// any value is an override, 0 included.
function overrideOrNull(value, periodDefault) {
  const parsed = value === '' || value == null ? null : Number(value);
  if (parsed == null || !Number.isFinite(parsed)) return null;
  return periodDefault != null && parsed === Number(periodDefault)
    ? null
    : parsed;
}

function minStayOverrideOrNull(value, periodDefault) {
  const parsed = overrideOrNull(value, periodDefault);
  return parsed != null && parsed >= 1 ? parsed : null;
}

// A night Room rates does not sell (Closed season / room Not offered): greyed
// on every row, never selectable. The rate row names the status and season.
const STATUS_CELL_CLASSES = {
  [NightStatusEnum.CLOSED]: 'bg-gray-200/70 text-gray-700',
  [NightStatusEnum.NOT_OFFERED]: 'bg-gray-100 text-gray-500',
};
const STATUS_LABEL_KEYS = {
  [NightStatusEnum.CLOSED]: 'nightRatesEditor.closed',
  [NightStatusEnum.NOT_OFFERED]: 'nightRatesEditor.notOffered',
};

function NightStatusCell({ status, label, className }) {
  return (
    <td
      className={[
        'px-2 text-center border-r border-gray-200',
        STATUS_CELL_CLASSES[status.kind],
        className,
      ].join(' ')}
    >
      {label && (
        <div className="flex flex-col items-center leading-tight">
          <span className="text-[11px] font-semibold">{label}</span>
          <span className="max-w-[90px] truncate text-[10px] font-normal">
            {status.season.name}
          </span>
        </div>
      )}
    </td>
  );
}

// `seasonModel` (the contract's saved season model, contract page only) lets
// nights outside every period say why: Closed season or room Not offered.
export default function NightRatesEditor({
  canUpdateHotel = true,
  currency,
  contract,
  seasonModel,
  hotelId,
}) {
  const { t } = useTranslation(['contracts', 'allotments', 'misc']);
  const { currentOrganizationId } = useApp();
  const { notify } = useSnack();
  // The night-rate endpoint binds hotelId as a plain numeric route param, so it
  // needs the RAW id even when the caller passes an idService-encoded one. The id
  // is supplied by the parent so the editor works both inside a single contract
  // (EditContract) and standalone with a hotel picker (NightRates page).
  const rawHotelId = decodeId(hotelId);

  // Current month state (defaults to current month)
  const [currentMonth, setCurrentMonth] = useState(dayjs().month());

  // Organization selector state. `dmcOrgId` is the logged-in DMC organization,
  // used both as the default selection and to resolve DMC-scoped promotions.
  const [organizationId, setOrganizationId] = useState(null);
  const dmcOrgId = decodeId(currentOrganizationId);
  const selectedOrganizationId = organizationId ?? dmcOrgId;

  // Selected days state: array of keys "roomId_dateStr"
  const [selectedKeys, setSelectedKeys] = useState([]);

  const [extraOrgIds, setExtraOrgIds] = useState([]);
  const [includeCurrentOrg, setIncludeCurrentOrg] = useState(true);
  const [targetOrgsModalOpen, setTargetOrgsModalOpen] = useState(false);

  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
    setExtraOrgIds([]);
    setIncludeCurrentOrg(true);
  }, []);

  // Room the grid is narrowed to; null shows every room of the contract.
  const [selectedRoomId, setSelectedRoomId] = useState(null);

  // Supplements modal state: { dateStr, roomName } for the clicked cell, or null
  // when closed. Opened from a supplements badge instead of the former tooltip.
  const [supplementsModal, setSupplementsModal] = useState(null);

  // All operational promotions of this hotel. Type / applicability filtering is
  // done per cell on the client (see getCellPromotions) so the same list serves
  // every organization / room / day.
  const { data, isLoading } = useAsync({
    promiseFn: getPromotions,
    productId: rawHotelId,
    state: 'operational',
    kind: ProductTypeEnum.HOTEL,
    type: SUPPLEMENT_PROMOTION_TYPES,
    mandatorySupplement: true,
    perPage: 200,
    watchFn: (prev, next) => prev.productId !== next.productId,
  });
  const promotions = data?.data ?? [];

  const hotelRoomContracts = contract?.hotelRoomContracts;

  const { organizations: allOrganizations } =
    useNightRatesOrganizations(hotelRoomContracts);

  // Calculate days in the selected month
  const daysInMonth = useMemo(() => {
    return dayjs().month(currentMonth).daysInMonth();
  }, [currentMonth]);

  const daysList = useMemo(() => {
    return [...Array(daysInMonth)].map((_, i) =>
      dayjs()
        .month(currentMonth)
        .date(i + 1),
    );
  }, [currentMonth, daysInMonth]);

  // First / last day of the displayed month, driving both the grid and the
  // night-rate fetch window.
  const from = useMemo(
    () => toDateString(dayjs().month(currentMonth).startOf('month')),
    [currentMonth],
  );
  const to = useMemo(
    () => toDateString(dayjs().month(currentMonth).endOf('month')),
    [currentMonth],
  );

  // Stored per-night overrides for the displayed month, refetched whenever the
  // window, hotel, or organization changes. The server only exposes them for PUBLISHED
  // contracts; drafts simply fall back to their period defaults.
  const {
    data: nightRatesData,
    reload: reloadNightRates,
    isPending,
  } = useAsync({
    promiseFn: getHotelNightRates,
    hotelId: rawHotelId,
    from,
    to,
    organizationId: selectedOrganizationId,
    watchFn: (prev, next) =>
      prev.from !== next.from ||
      prev.to !== next.to ||
      prev.hotelId !== next.hotelId ||
      prev.organizationId !== next.organizationId,
  });

  // Keep the last data while a refetch is pending, so enableReinitialize does
  // not reset the grid to period defaults mid-flight (e.g. org change).
  const stableDataRef = useRef(nightRatesData);
  if (!isPending && nightRatesData !== stableDataRef.current) {
    stableDataRef.current = nightRatesData;
  }
  const resolvedNightRates = isPending ? stableDataRef.current : nightRatesData;

  // Track Formik dirty state from inside the render prop so month/org guards
  // can warn the user before discarding unsaved edits.
  const dirtyRef = useRef(false);

  // Warn when leaving the page with unsaved changes.
  useEffect(() => {
    const handler = (event) => {
      if (dirtyRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Guards for month / org changes: warn when there are unsaved edits.
  const guardedSetCurrentMonth = useCallback(
    (month) => {
      if (
        dirtyRef.current &&
        !window.confirm(t('nightRatesEditor.dirtyConfirm'))
      ) {
        return;
      }
      setCurrentMonth(month);
      clearSelection();
    },
    [t, clearSelection],
  );

  const guardedSetOrganizationId = useCallback(
    (id) => {
      if (
        dirtyRef.current &&
        !window.confirm(t('nightRatesEditor.dirtyConfirm'))
      ) {
        return;
      }
      setOrganizationId(id);
      setExtraOrgIds([]);
      setIncludeCurrentOrg(true);
    },
    [t],
  );

  // Overrides keyed by "rawPeriodId_YYYY-MM-DD" — a night belongs to exactly one
  // period, so this uniquely resolves each cell.
  const overridesMap = useMemo(() => {
    const map = new Map();
    (resolvedNightRates?.data ?? []).forEach((room) => {
      (room.nights ?? []).forEach((night) => {
        const recordOrganizationId =
          night.organizationId ?? room.organizationId;
        if (
          recordOrganizationId != null &&
          String(decodeId(recordOrganizationId)) !==
            String(selectedOrganizationId)
        ) {
          return;
        }
        map.set(`${night.periodId}_${night.day.slice(0, 10)}`, night);
      });
    });
    return map;
  }, [resolvedNightRates, selectedOrganizationId]);

  // Process rooms prop passed from contract. Period ids are decoded to their raw
  // form here because that is what the night-rate endpoint expects on write.
  const activeRooms = useMemo(() => {
    if (
      !hotelRoomContracts ||
      !Array.isArray(hotelRoomContracts) ||
      hotelRoomContracts.length === 0
    ) {
      return [];
    }

    return hotelRoomContracts.map((r, index) => {
      const periods = (Array.isArray(r.periods) ? r.periods : []).map((p) => ({
        id: String(decodeId(p.id)),
        startAt: p.startAt,
        endAt: p.endAt,
        fitPrice: Number(p.fitPrice ?? p.gitPrice ?? 0),
        // null = group price not set in Room rates: no group sale, not 0.
        gitPrice: p.gitPrice == null ? null : Number(p.gitPrice),
        // A period without its own minimum stay sells with the contract's.
        minimumStay: Number(p.minimumStay ?? contract?.minimumStay ?? 1),
        // Set per period by the standalone editor (periods can come from several
        // contracts); inside a single contract it falls back to the contract flag.
        enableGroupPrices: Boolean(
          p.enableGroupPrices ?? contract?.enableGroupPrices,
        ),
        organizationIds: (p.periodOrganizations ?? [])
          .map((po) => String(decodeId(po?.organization?.id)))
          .filter((id) => id && id !== 'undefined' && id !== 'null'),
      }));

      return {
        id: String(r.id || `room-${index}`),
        // The season model keys rooms by raw hotel room contract id.
        contractRoomId: String(decodeId(r.id)),
        rawRoomId: String(decodeId(r.hotelRoom?.id ?? r.id)),
        name: r.hotelRoom?.displayName || `Room ${index + 1}`,
        periods,
      };
    });
  }, [hotelRoomContracts, contract?.enableGroupPrices, contract?.minimumStay]);

  // The month offset the grid should open on, resolved from whichever range the
  // entry point provides. Opening always defaulted to today's month, so a contract
  // starting next January landed on an empty grid with nothing telling the DMC
  // that its periods live several clicks forward.
  const defaultMonth = useMemo(() => {
    const today = dayjs();
    // Same unit as `currentMonth`: months since January of the CURRENT year, so
    // the value stays negative / above 11 for other years and rolls over.
    const asOffset = (date) =>
      (dayjs(date).year() - today.year()) * 12 + dayjs(date).month();

    // Opened from a contract: EditContract passes that contract, which carries its
    // own range, while the standalone editor passes a merged shell that has none.
    // Anchor on the contract itself, clamping today into its range so an ongoing
    // contract still opens on the month the DMC is actually working in.
    if (contract?.startAt) {
      if (today.isBefore(dayjs(contract.startAt), 'day')) {
        return asOffset(contract.startAt);
      }
      if (contract.endAt && today.isAfter(dayjs(contract.endAt), 'day')) {
        return asOffset(contract.endAt);
      }
      return asOffset(today);
    }

    // Opened from the hotel: the grid aggregates every active contract, so the
    // periods themselves are the only range available.
    const periods = activeRooms.flatMap((room) => room.periods);
    if (periods.length === 0) return null;

    const coversToday = periods.some((p) =>
      today.isBetween(dayjs(p.startAt), dayjs(p.endAt), 'day', '[]'),
    );
    if (coversToday) return asOffset(today);

    const upcoming = periods.filter((p) =>
      dayjs(p.startAt).isAfter(today, 'day'),
    );
    if (upcoming.length > 0) {
      const earliest = upcoming.reduce((best, p) =>
        dayjs(p.startAt).isBefore(dayjs(best.startAt)) ? p : best,
      );
      return asOffset(earliest.startAt);
    }

    // Contract entirely in the past: land on its last month rather than a blank
    // grid the DMC would have to walk backwards from.
    const latest = periods.reduce((best, p) =>
      dayjs(p.endAt).isAfter(dayjs(best.endAt)) ? p : best,
    );
    return asOffset(latest.endAt);
  }, [activeRooms, contract?.startAt, contract?.endAt]);

  // Position the grid once per subject, as soon as its dates are known — the
  // contract can still be loading on the first render, so this cannot be a
  // useState initializer. Keyed on the contract (EditContract, where the same
  // hotel has several) falling back to the hotel (standalone picker), so a switch
  // repositions while month navigation within one subject is never overridden.
  const positioningKey = contract?.id ?? rawHotelId;
  const positionedFor = useRef(null);
  useEffect(() => {
    if (defaultMonth === null) return;
    if (positionedFor.current === positioningKey) return;
    positionedFor.current = positioningKey;
    setCurrentMonth(defaultMonth);
    clearSelection();
  }, [defaultMonth, positioningKey, clearSelection]);

  // The rooms the grid shows. Formik still indexes its values by the position in
  // `activeRooms`, so narrowing only changes what is rendered and what the
  // selection helpers below span — never a field path.
  const visibleRooms = useMemo(
    () =>
      selectedRoomId
        ? activeRooms.filter((room) => room.id === selectedRoomId)
        : activeRooms,
    [activeRooms, selectedRoomId],
  );

  // Switching rooms drops the current selection: a bulk edit must never reach a
  // night the DMC can no longer see.
  const selectRoom = (roomId) => {
    setSelectedRoomId(roomId);
    clearSelection();
  };

  // Per (roomIndex, dayIndex): the covering period (raw id) and its default rate /
  // min stay. A null periodId marks a day outside every contract period — it can
  // neither be edited nor carry an override.
  // When multiple periods cover the same date (overlapping contracts), the one
  // with the latest startAt wins — deterministic regardless of fetch order.
  const hasSeasons = Boolean(seasonModel?.seasons?.length);
  const dayMeta = useMemo(() => {
    return activeRooms.map((room) =>
      daysList.map((date) => {
        const period = room.periods
          .filter(
            (p) =>
              date.isSameOrAfter(dayjs(p.startAt), 'day') &&
              date.isSameOrBefore(dayjs(p.endAt), 'day'),
          )
          .reduce(
            (best, p) =>
              !best || dayjs(p.startAt).isAfter(dayjs(best.startAt)) ? p : best,
            null,
          );
        if (!period) {
          return {
            periodId: null,
            defaultRate: null,
            defaultGitRate: null,
            defaultMinStay: null,
            enableGroupPrices: false,
            organizationIds: [],
            status: hasSeasons
              ? nightStatusOf(
                  seasonModel,
                  room.contractRoomId,
                  toDateString(date),
                )
              : null,
          };
        }
        return {
          periodId: period.id,
          defaultRate: period.fitPrice,
          defaultGitRate: period.gitPrice,
          defaultMinStay: period.minimumStay,
          enableGroupPrices: period.enableGroupPrices,
          organizationIds: period.organizationIds,
          status: null,
        };
      }),
    );
  }, [activeRooms, daysList, hasSeasons, seasonModel]);

  // Formik keeps one editable value per room and per day. Each cell starts from
  // its period default and is overlaid with any stored override.
  const initialValues = useMemo(
    () => ({
      rooms: activeRooms.map((room, roomIndex) => ({
        roomId: room.id,
        days: daysList.map((date, dayIndex) => {
          const meta = dayMeta[roomIndex]?.[dayIndex];
          if (!meta?.periodId) {
            return { rate: '', gitRate: '', minimumStay: '' };
          }
          const override = overridesMap.get(
            `${meta.periodId}_${toDateString(date)}`,
          );
          return {
            rate:
              override?.fitPrice != null ? override.fitPrice : meta.defaultRate,
            // Only meaningful on a group-priced period; elsewhere the cell is not
            // rendered and the value is derived from the fit rate on save.
            gitRate:
              override?.gitPrice != null
                ? override.gitPrice
                : (meta.defaultGitRate ?? ''),
            minimumStay:
              override?.minimumStay != null
                ? override.minimumStay
                : meta.defaultMinStay,
          };
        }),
      })),
    }),
    [activeRooms, daysList, dayMeta, overridesMap],
  );

  // Per room: does the visible month contain at least one group-priced period?
  // Drives the group-rate sub-row, so a contract without group prices keeps the
  // two-row grid it has today and gains no empty column.
  const roomHasGroupPrices = useMemo(
    () =>
      activeRooms.map((_room, roomIndex) =>
        daysList.some(
          (_date, dayIndex) =>
            dayMeta[roomIndex]?.[dayIndex]?.enableGroupPrices,
        ),
      ),
    [activeRooms, daysList, dayMeta],
  );

  // Only nights inside a period can be edited, so only they can be selected:
  // Closed, Not offered and out-of-contract nights never count as selected.
  const isSelectable = (roomId, dateStr) => {
    const roomIndex = activeRooms.findIndex((room) => room.id === roomId);
    const dayIndex = daysList.findIndex((d) => toDateString(d) === dateStr);
    return Boolean(dayMeta[roomIndex]?.[dayIndex]?.periodId);
  };

  // Helper to toggle single cell selection
  const toggleSelectCell = (roomId, dateStr) => {
    if (!isSelectable(roomId, dateStr)) return;
    const key = `${roomId}_${dateStr}`;
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  // Column / select-all span the rooms ON SCREEN, so they mean the same thing
  // whether the grid shows the whole contract or a single room.
  const columnKeysOf = (dateStr) =>
    visibleRooms
      .filter((room) => isSelectable(room.id, dateStr))
      .map((room) => `${room.id}_${dateStr}`);

  const isDateColumnSelected = (dateStr) => {
    const columnKeys = columnKeysOf(dateStr);
    return (
      columnKeys.length > 0 && columnKeys.every((k) => selectedKeys.includes(k))
    );
  };

  // Toggle selection for an entire date column
  const toggleDateColumn = (dateStr) => {
    const columnKeys = columnKeysOf(dateStr);
    if (isDateColumnSelected(dateStr)) {
      setSelectedKeys((prev) => prev.filter((k) => !columnKeys.includes(k)));
    } else {
      setSelectedKeys((prev) => Array.from(new Set([...prev, ...columnKeys])));
    }
  };

  // Toggle selection for all cells in month
  const allKeys = daysList.flatMap((d) => columnKeysOf(toDateString(d)));
  const isAllSelected =
    allKeys.length > 0 && allKeys.every((k) => selectedKeys.includes(k));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      clearSelection();
    } else {
      setSelectedKeys(allKeys);
    }
  };

  // Apply bulk changes to selected keys through Formik — builds the rooms
  // array once, then a single setFieldValue to avoid N×M re-renders. Resets
  // the bulk inputs after application so a second click is idempotent.
  const handleApplyBulk = (values, setFieldValue) => {
    if (selectedKeys.length === 0) return;

    const nextRooms = values.rooms.map((room) => ({
      ...room,
      days: room.days.map((day) => ({ ...day })),
    }));

    selectedKeys.forEach((key) => {
      const separatorIndex = key.lastIndexOf('_');
      const roomId = key.slice(0, separatorIndex);
      const dateStr = key.slice(separatorIndex + 1);
      const roomIndex = activeRooms.findIndex((room) => room.id === roomId);
      const dayIndex = daysList.findIndex(
        (date) => toDateString(date) === dateStr,
      );

      if (roomIndex === -1 || dayIndex === -1) return;

      // Skip days outside any period — they are not editable and are dropped on
      // save anyway.
      if (!dayMeta[roomIndex]?.[dayIndex]?.periodId) return;

      if (values.bulkRate !== '') {
        nextRooms[roomIndex].days[dayIndex].rate = values.bulkRate;
      }
      // Skip nights whose period has no group prices, or no group price set in
      // Room rates: the grid does not let these cells be edited.
      if (
        values.bulkGitRate !== '' &&
        dayMeta[roomIndex][dayIndex].enableGroupPrices &&
        dayMeta[roomIndex][dayIndex].defaultGitRate != null
      ) {
        nextRooms[roomIndex].days[dayIndex].gitRate = values.bulkGitRate;
      }
      if (values.bulkMinStay !== '') {
        nextRooms[roomIndex].days[dayIndex].minimumStay = values.bulkMinStay;
      }
    });

    setFieldValue('rooms', nextRooms);
    setFieldValue('bulkRate', '');
    setFieldValue('bulkGitRate', '');
    setFieldValue('bulkMinStay', '');
  };

  // The bulk group-rate input only appears when the selection actually spans a
  // group-priced night, so it never offers an edit that save would drop.
  const selectionHasGroupPrices = useMemo(
    () =>
      selectedKeys.some((key) => {
        const separatorIndex = key.lastIndexOf('_');
        const roomId = key.slice(0, separatorIndex);
        const dateStr = key.slice(separatorIndex + 1);
        const roomIndex = activeRooms.findIndex((room) => room.id === roomId);
        const dayIndex = daysList.findIndex(
          (date) => toDateString(date) === dateStr,
        );
        const meta = dayMeta[roomIndex]?.[dayIndex];
        return Boolean(meta?.enableGroupPrices && meta.defaultGitRate != null);
      }),
    [selectedKeys, activeRooms, daysList, dayMeta],
  );

  const pickerSelection = useMemo(
    () =>
      includeCurrentOrg && selectedOrganizationId
        ? [selectedOrganizationId, ...extraOrgIds]
        : extraOrgIds,
    [includeCurrentOrg, selectedOrganizationId, extraOrgIds],
  );

  const setPickerSelection = useCallback(
    (next) => {
      setIncludeCurrentOrg(
        next.some((v) => String(v) === String(selectedOrganizationId)),
      );
      setExtraOrgIds(
        next.filter((v) => String(v) !== String(selectedOrganizationId)),
      );
    },
    [selectedOrganizationId],
  );

  // Selected date range text for floating bar
  const selectedSummary = useMemo(() => {
    if (selectedKeys.length === 0) return '';
    const datesSet = new Set(
      selectedKeys.map((k) => k.slice(k.lastIndexOf('_') + 1)),
    );
    const sortedDates = Array.from(datesSet).sort();

    const uniqueDaysCount = datesSet.size;
    let rangeText = '';
    if (sortedDates.length === 1) {
      rangeText = formatDate(sortedDates[0], 'D MMM');
    } else if (sortedDates.length > 1) {
      const first = formatDate(sortedDates[0], 'D');
      const last = formatDate(sortedDates[sortedDates.length - 1], 'D MMM');
      rangeText = `${first}–${last}`;
    }

    // The scope note names the room when the grid is narrowed to one, since the
    // bulk edit then stops at that room.
    const scope =
      visibleRooms.length === activeRooms.length
        ? t('nightRatesEditor.appliesToAllRooms')
        : visibleRooms.map((room) => room.name).join(', ');

    return `${t('nightRatesEditor.nightsSelected', {
      count: uniqueDaysCount,
    })} (${rangeText}) · ${scope}`;
  }, [selectedKeys, visibleRooms, activeRooms, t]);

  // Supplement / supplement-percent promotions applicable to a given room + day
  // for the currently selected organization. Filtering mirrors the booking rules:
  // benefit kind, the promotion's date range + weekday availability, the rooms it
  // targets, the organization it is distributed to, and the night's minimum stay.
  const getCellPromotions = useCallback(
    (roomRawId, date, minimumStay = 1) => {
      const dateStr = toDateString(date);
      const weekday = WEEKDAY_NAMES[(date.day() + 6) % 7];

      return promotions.filter((promo) => {
        if (!SUPPLEMENT_BENEFIT_KINDS.includes(promo.benefit?.kind)) {
          return false;
        }

        // Stay length: only supplements covering at least this night's minimum
        // stay are listed on it — a night forced to 3 nights never shows a
        // supplement conditioned on fewer nights.
        if (promotionNightsCondition(promo) > minimumStay) {
          return false;
        }

        // Day: within one of the promotion's applicable periods (date range) and
        // on an allowed weekday (empty list = every weekday).
        const dayApplies = (promo.applicablePeriods ?? []).some((period) => {
          if (period.startAt && dateStr < period.startAt) return false;
          if (period.endAt && dateStr > period.endAt) return false;
          const days = period.days ?? [];
          return days.length === 0 || days.includes(weekday);
        });
        if (!dayApplies) return false;

        // Room: applies to every room, or explicitly to this one.
        const perRoom = promo.benefit?.promoPerRoom;
        const applicableRooms = promo.benefit?.applicableRooms ?? [];
        const roomApplies =
          !perRoom ||
          applicableRooms.some(
            (r) => String(decodeId(r.id)) === String(roomRawId),
          );
        if (!roomApplies) return false;

        // Organization: the offer lists its recipients, the DMC included.
        return (promo.organizationIds ?? []).some(
          (id) => Number(decodeId(id)) === Number(selectedOrganizationId),
        );
      });
    },
    [promotions, selectedOrganizationId],
  );

  // Promotions listed in the supplements modal for the clicked cell.
  const modalPromotions = useMemo(() => {
    if (!supplementsModal) return [];
    return getCellPromotions(
      supplementsModal.roomRawId,
      dayjs(supplementsModal.dateStr),
      supplementsModal.minimumStay,
    );
  }, [supplementsModal, getCellPromotions]);

  const handleSave = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = (values.rooms ?? [])
        .map((room, roomIndex) => {
          const nights = (room.days ?? [])
            .map((day, dayIndex) => {
              const meta = dayMeta[roomIndex]?.[dayIndex];
              // Days outside any period can never carry an override.
              if (!meta?.periodId) {
                return null;
              }

              const dateStr = toDateString(daysList[dayIndex]);
              const fitPrice = overrideOrNull(day.rate, meta.defaultRate);
              const minimumStay = minStayOverrideOrNull(
                day.minimumStay,
                meta.defaultMinStay,
              );
              const editedGitPrice = overrideOrNull(
                day.gitRate,
                meta.defaultGitRate,
              );

              // With group prices enabled the two axes are priced separately and the
              // grid edits both, so each persists against its OWN period default —
              // they can legitimately differ, and one can deviate while the other
              // inherits. Without group prices the git price carries no meaning of
              // its own, so it mirrors the fit override — otherwise a group booking
              // would ignore the override and fall back to the period default.
              const gitPrice = meta.enableGroupPrices
                ? editedGitPrice
                : fitPrice;
              const existing = overridesMap.get(`${meta.periodId}_${dateStr}`);

              const isOverride =
                fitPrice != null || gitPrice != null || minimumStay != null;

              // Nothing to upsert and no prior override to clear → skip. A fully
              // null night that previously had an override is kept so the server
              // clears it back to the period default.
              if (!isOverride && !existing) {
                return null;
              }

              return {
                day: dateStr,
                periodId: meta.periodId,
                fitPrice,
                gitPrice,
                minimumStay,
              };
            })
            .filter(Boolean);

          return {
            id: activeRooms[roomIndex]?.rawRoomId,
            organizationId: String(selectedOrganizationId),
            nights,
          };
        })
        .filter((room) => room.nights.length > 0);

      const mirrorPayload = extraOrgIds.flatMap((orgId) => {
        const nightsByRoom = new Map();

        selectedKeys.forEach((key) => {
          const separatorIndex = key.lastIndexOf('_');
          const roomId = key.slice(0, separatorIndex);
          const dateStr = key.slice(separatorIndex + 1);
          const roomIndex = activeRooms.findIndex((r) => r.id === roomId);
          const dayIndex = daysList.findIndex(
            (d) => toDateString(d) === dateStr,
          );
          if (roomIndex === -1 || dayIndex === -1) return;

          const meta = dayMeta[roomIndex]?.[dayIndex];
          if (!meta?.periodId) return;
          if (!meta.organizationIds.includes(String(orgId))) return;

          const day = values.rooms?.[roomIndex]?.days?.[dayIndex];
          if (!day) return;

          const fitPrice = overrideOrNull(day.rate, meta.defaultRate);
          const gitPrice = meta.enableGroupPrices
            ? overrideOrNull(day.gitRate, meta.defaultGitRate)
            : fitPrice;
          const minimumStay = minStayOverrideOrNull(
            day.minimumStay,
            meta.defaultMinStay,
          );
          if (fitPrice == null && gitPrice == null && minimumStay == null) {
            return;
          }

          const rawRoomId = activeRooms[roomIndex].rawRoomId;
          if (!nightsByRoom.has(rawRoomId)) nightsByRoom.set(rawRoomId, []);
          nightsByRoom.get(rawRoomId).push({
            day: dateStr,
            periodId: meta.periodId,
            fitPrice,
            gitPrice,
            minimumStay,
          });
        });

        return [...nightsByRoom.entries()].map(([roomId, roomNights]) => ({
          id: roomId,
          organizationId: String(orgId),
          nights: roomNights,
        }));
      });

      const fullPayload = [
        ...(includeCurrentOrg ? payload : []),
        ...mirrorPayload,
      ];

      if (fullPayload.length === 0) {
        notify(t('nightRatesEditor.saveSuccess'));
        clearSelection();
        if (!includeCurrentOrg) resetForm();
        reloadNightRates();
        return;
      }

      await updateHotelNightRates({
        hotelId: rawHotelId,
        payload: fullPayload,
      });

      notify(t('nightRatesEditor.saveSuccess'));
      clearSelection();
      if (!includeCurrentOrg) resetForm();
      reloadNightRates();
    } catch (error) {
      if (error?.response?.status === 409) {
        notify(t('nightRatesEditor.saveConflict'), { type: 'error' });
      } else if (error?.response?.data?.message) {
        notify(error.response.data.message, { type: 'error' });
      } else {
        notify(t('misc:saveError'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // style (not className) is how KoobCard exposes layout overrides — a min
    // height keeps the section filling the page below it even when only a few
    // rooms are shown, and the flex column lets the footer sit at the bottom.
    <KoobCard
      style={{
        minHeight: 'calc(100vh - 220px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <KoobTitle>{t('nightRatesEditor.title')}</KoobTitle>
      <KoobParagraph>{t('nightRatesEditor.description')}</KoobParagraph>

      <Formik
        initialValues={{
          ...initialValues,
          bulkRate: '',
          bulkGitRate: '',
          bulkMinStay: '',
        }}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({ values, setFieldValue, resetForm, isSubmitting, dirty }) => {
          dirtyRef.current = dirty;
          return (
            <Form className="mt-5 flex flex-1 flex-col overflow-hidden">
              <FormNavigationBlocker />
              <AllotmentsEditorMonthSelector
                currentMonth={currentMonth}
                setCurrentMonth={guardedSetCurrentMonth}
                isDisabled={false}
                reload={() => {}}
              />

              <div className="my-3 flex-1">
                {/* Grid scope: the room the grid is narrowed to, and the
                  organization the overrides are written for. */}
                <div className="bg-gray-50 border-t border-x border-gray-200 rounded-t-md">
                  <div className="p-3 w-full flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium text-gray-600">
                        {t('nightRatesEditor.distributeTo')}
                      </span>
                      <div className="w-56">
                        <NightRatesOrganizationSelector
                          rooms={hotelRoomContracts}
                          organizationId={organizationId}
                          setOrganizationId={guardedSetOrganizationId}
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium text-gray-600">
                        {t('nightRatesEditor.room')}
                      </span>
                      <div className="w-56">
                        <NightRatesRoomSelector
                          rooms={activeRooms}
                          roomId={selectedRoomId}
                          setRoomId={selectRoom}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-md rounded-t-none border border-gray-200 overflow-hidden">
                  <div className="min-h-[25vh] inline-block min-w-full">
                    <table className="min-w-full border-collapse text-left text-xs">
                      {/* Table Header: Column headers with date checkboxes */}
                      <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                        <tr>
                          <th className="py-3 px-4 w-56 sticky left-0 z-20 bg-gray-50 border-r border-gray-200 font-semibold text-gray-700">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={toggleSelectAll}
                                className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                              />
                              <span>{t('nightRatesEditor.selectNights')}</span>
                            </div>
                          </th>

                          {daysList.map((date) => {
                            const dateStr = toDateString(date);
                            const isColSelected = isDateColumnSelected(dateStr);
                            const isWeekend =
                              date.day() === 0 || date.day() === 6;

                            return (
                              <th
                                key={dateStr}
                                onClick={() => toggleDateColumn(dateStr)}
                                className={[
                                  'py-2 px-3 text-center min-w-[100px] border-r border-gray-200 cursor-pointer transition-colors select-none',
                                  isColSelected
                                    ? 'bg-orange-50 border-t-2 border-t-orange-500 font-bold text-orange-700'
                                    : isWeekend
                                      ? 'bg-gray-100/60 text-gray-700'
                                      : 'hover:bg-gray-100',
                                ].join(' ')}
                              >
                                <div className="flex flex-col items-center space-y-1">
                                  <input
                                    type="checkbox"
                                    checked={isColSelected}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleDateColumn(dateStr);
                                    }}
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer"
                                  />
                                  <span className="text-[11px] font-normal text-gray-500 uppercase">
                                    {formatDate(date, 'ddd')}
                                  </span>
                                  <span className="font-semibold text-gray-800">
                                    {formatDate(date, 'D MMM')}
                                  </span>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200">
                        {visibleRooms.length > 0 ? (
                          visibleRooms.map((room) => (
                            <React.Fragment key={room.id}>
                              {/* Room Row Group Header */}
                              <tr className="bg-gray-50/70 border-t border-b border-gray-200 font-semibold text-gray-800">
                                <td
                                  colSpan={daysList.length + 1}
                                  className="py-2.5 px-4 sticky left-0 z-10 bg-gray-50/90"
                                >
                                  {/* The cell spans the whole row, so it can't pin
                                    its own content; the label is inline-flex +
                                    sticky left-0 so the room name stays visible
                                    at the left edge on horizontal scroll. */}
                                  <div className="inline-flex items-center space-x-2 sticky left-4">
                                    <i className="far fa-bed text-orange-500" />
                                    <span className="text-sm font-semibold">
                                      {room.name}
                                    </span>
                                  </div>
                                </td>
                              </tr>

                              {/* Sub-row 1: Rate / night */}
                              <tr
                                className="hover:bg-gray-50/40"
                                data-testid={`night-row-${room.rawRoomId}`}
                              >
                                <td className="py-2.5 px-4 sticky left-0 z-10 bg-white border-r border-gray-200 font-medium text-gray-600 pl-8">
                                  {t('nightRatesEditor.ratePerNight')}
                                </td>

                                {daysList.map((date) => {
                                  const roomIndex = activeRooms.indexOf(room);
                                  const dayIndex = daysList.indexOf(date);
                                  const dateStr = toDateString(date);
                                  const key = `${room.id}_${dateStr}`;
                                  const isSelected = selectedKeys.includes(key);
                                  const meta = dayMeta[roomIndex]?.[dayIndex];
                                  const hasPeriod = Boolean(meta?.periodId);
                                  const rate = getIn(
                                    values,
                                    `rooms[${roomIndex}].days[${dayIndex}].rate`,
                                  );
                                  const isOverridden =
                                    hasPeriod &&
                                    Number(rate) !== Number(meta.defaultRate);

                                  if (meta?.status) {
                                    return (
                                      <NightStatusCell
                                        key={dateStr}
                                        status={meta.status}
                                        label={t(
                                          STATUS_LABEL_KEYS[meta.status.kind],
                                        )}
                                        className="py-1.5"
                                      />
                                    );
                                  }

                                  if (!hasPeriod) {
                                    return (
                                      <td
                                        key={dateStr}
                                        className="py-2 px-2 text-center border-r border-gray-200 bg-gray-50/40 text-gray-300"
                                      >
                                        &mdash;
                                      </td>
                                    );
                                  }

                                  return (
                                    <td
                                      key={dateStr}
                                      data-testid={`night-${dateStr}`}
                                      onClick={() =>
                                        toggleSelectCell(room.id, dateStr)
                                      }
                                      className={[
                                        'py-2 px-2 text-center border-r border-gray-200 cursor-pointer transition-all',
                                        isSelected
                                          ? 'bg-orange-50/60 ring-2 ring-orange-400 ring-inset font-bold text-orange-900'
                                          : isOverridden
                                            ? 'bg-amber-50/30 font-semibold text-gray-900'
                                            : 'text-gray-700 hover:bg-gray-50',
                                      ].join(' ')}
                                    >
                                      <div className="flex items-center justify-center space-x-1">
                                        <EditableCellValue
                                          name={`rooms[${activeRooms.indexOf(
                                            room,
                                          )}].days[${daysList.indexOf(
                                            date,
                                          )}].rate`}
                                          value={rate}
                                          min={0}
                                          onValueChange={setFieldValue}
                                          displayClassName="min-w-[3rem] rounded border-0 bg-transparent px-1.5 py-1 text-xs font-medium focus:outline-none hover:bg-orange-100/60 hover:text-orange-700 transition-colors"
                                        />
                                        <span>{currency}</span>
                                        {isOverridden && (
                                          <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>

                              {/* Sub-row 2: Group rate / night — only on rooms whose
                                  month has a group-priced period. The fit and git
                                  rates are independent axes: editing one never
                                  moves the other. */}
                              {roomHasGroupPrices[
                                activeRooms.indexOf(room)
                              ] && (
                                <tr className="hover:bg-gray-50/40">
                                  <td className="py-2.5 px-4 sticky left-0 z-10 bg-white border-r border-gray-200 font-medium text-gray-600 pl-8">
                                    {t('nightRatesEditor.groupRatePerNight')}
                                  </td>

                                  {daysList.map((date) => {
                                    const roomIndex = activeRooms.indexOf(room);
                                    const dayIndex = daysList.indexOf(date);
                                    const dateStr = toDateString(date);
                                    const key = `${room.id}_${dateStr}`;
                                    const isSelected =
                                      selectedKeys.includes(key);
                                    const meta = dayMeta[roomIndex]?.[dayIndex];
                                    // A day outside any period, or inside a period
                                    // whose contract has no group prices, has no
                                    // group rate to edit.
                                    const hasGitRate = Boolean(
                                      meta?.periodId && meta.enableGroupPrices,
                                    );
                                    const gitRate = getIn(
                                      values,
                                      `rooms[${roomIndex}].days[${dayIndex}].gitRate`,
                                    );
                                    // No group price in Room rates: read-only, so
                                    // a night cannot sell groups its season does
                                    // not. A stored night override still shows.
                                    const isNotSet =
                                      hasGitRate && meta.defaultGitRate == null;
                                    const isOverridden =
                                      hasGitRate &&
                                      (isNotSet
                                        ? gitRate !== '' && gitRate != null
                                        : Number(gitRate) !==
                                          Number(meta.defaultGitRate));

                                    if (meta?.status) {
                                      return (
                                        <NightStatusCell
                                          key={dateStr}
                                          status={meta.status}
                                          className="py-2"
                                        />
                                      );
                                    }

                                    if (!hasGitRate) {
                                      return (
                                        <td
                                          key={dateStr}
                                          className="py-2 px-2 text-center border-r border-gray-200 bg-gray-50/40 text-gray-300"
                                        >
                                          &mdash;
                                        </td>
                                      );
                                    }

                                    // Read-only: never framed nor clickable like an
                                    // editable cell; a selected night only tints it.
                                    if (isNotSet) {
                                      return (
                                        <td
                                          key={dateStr}
                                          className={[
                                            'py-2 px-2 text-center border-r border-gray-200 cursor-default',
                                            isSelected ? 'bg-orange-50/60' : '',
                                          ].join(' ')}
                                        >
                                          <div className="flex items-center justify-center space-x-1">
                                            {isOverridden ? (
                                              <>
                                                <span className="px-1.5 py-1 text-gray-700">
                                                  {gitRate} {currency}
                                                </span>
                                                <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                                              </>
                                            ) : (
                                              <span className="px-1.5 py-1 italic text-gray-400">
                                                {t('nightRatesEditor.notSet')}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                      );
                                    }

                                    return (
                                      <td
                                        key={dateStr}
                                        onClick={() =>
                                          toggleSelectCell(room.id, dateStr)
                                        }
                                        className={[
                                          'py-2 px-2 text-center border-r border-gray-200 cursor-pointer transition-all',
                                          isSelected
                                            ? 'bg-orange-50/60 ring-2 ring-orange-400 ring-inset font-bold text-orange-900'
                                            : isOverridden
                                              ? 'bg-amber-50/30 font-semibold text-gray-900'
                                              : 'text-gray-700 hover:bg-gray-50',
                                        ].join(' ')}
                                      >
                                        <div className="flex items-center justify-center space-x-1">
                                          <EditableCellValue
                                            name={`rooms[${roomIndex}].days[${dayIndex}].gitRate`}
                                            value={gitRate}
                                            min={0}
                                            onValueChange={setFieldValue}
                                            displayClassName="min-w-[3rem] rounded border-0 bg-transparent px-1.5 py-1 text-xs font-medium focus:outline-none hover:bg-orange-100/60 hover:text-orange-700 transition-colors"
                                          />
                                          <span>{currency}</span>
                                          {isOverridden && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                                          )}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              )}

                              {/* Sub-row 3: Min stay */}
                              <tr className="hover:bg-gray-50/40">
                                <td className="py-2 px-4 sticky left-0 z-10 bg-white border-r border-gray-200 text-gray-500 pl-8 text-xs">
                                  {t('nightRatesEditor.minStay')}
                                </td>

                                {daysList.map((date) => {
                                  const roomIndex = activeRooms.indexOf(room);
                                  const dayIndex = daysList.indexOf(date);
                                  const dateStr = toDateString(date);
                                  const key = `${room.id}_${dateStr}`;
                                  const isSelected = selectedKeys.includes(key);
                                  const meta = dayMeta[roomIndex]?.[dayIndex];
                                  const hasPeriod = Boolean(meta?.periodId);
                                  const minStay = getIn(
                                    values,
                                    `rooms[${roomIndex}].days[${dayIndex}].minimumStay`,
                                  );

                                  if (meta?.status) {
                                    return (
                                      <NightStatusCell
                                        key={dateStr}
                                        status={meta.status}
                                        className="py-1.5"
                                      />
                                    );
                                  }

                                  if (!hasPeriod) {
                                    return (
                                      <td
                                        key={dateStr}
                                        className="py-1.5 px-2 text-center border-r border-gray-200 bg-gray-50/40 text-gray-300 text-xs"
                                      >
                                        &mdash;
                                      </td>
                                    );
                                  }

                                  return (
                                    <td
                                      key={dateStr}
                                      data-testid={`night-${dateStr}`}
                                      onClick={() =>
                                        toggleSelectCell(room.id, dateStr)
                                      }
                                      className={[
                                        'py-1.5 px-2 text-center border-r border-gray-200 cursor-pointer text-xs text-gray-600',
                                        isSelected
                                          ? 'bg-orange-50/60 font-semibold'
                                          : '',
                                      ].join(' ')}
                                    >
                                      <EditableCellValue
                                        name={`rooms[${activeRooms.indexOf(
                                          room,
                                        )}].days[${daysList.indexOf(
                                          date,
                                        )}].minimumStay`}
                                        value={minStay}
                                        min={1}
                                        integer
                                        onValueChange={setFieldValue}
                                        displayClassName="min-w-[2rem] rounded border-0 bg-transparent px-1.5 py-1 text-xs focus:outline-none hover:bg-orange-100/60 hover:text-orange-700 transition-colors"
                                      />
                                    </td>
                                  );
                                })}
                              </tr>

                              {/* Sub-row 4: Supplements */}
                              <tr className="hover:bg-gray-50/40 border-b border-gray-200">
                                <td className="py-2 px-4 sticky left-0 z-10 bg-white border-r border-gray-200 text-gray-500 pl-8 text-xs">
                                  {t('nightRatesEditor.supplements')}
                                </td>

                                {daysList.map((date) => {
                                  const roomIndex = activeRooms.indexOf(room);
                                  const dayIndex = daysList.indexOf(date);
                                  const dateStr = toDateString(date);
                                  const key = `${room.id}_${dateStr}`;
                                  const isSelected = selectedKeys.includes(key);
                                  const status =
                                    dayMeta[roomIndex]?.[dayIndex]?.status;
                                  // Nothing sells: no supplement to show either.
                                  if (status) {
                                    return (
                                      <NightStatusCell
                                        key={dateStr}
                                        status={status}
                                        className="py-1.5"
                                      />
                                    );
                                  }
                                  // The supplements shown on a night depend on the
                                  // minimum stay currently set on it, so read the
                                  // live form value rather than the saved override.
                                  const minimumStay = resolveMinStay(
                                    getIn(
                                      values,
                                      `rooms[${roomIndex}].days[${dayIndex}].minimumStay`,
                                    ),
                                    dayMeta[roomIndex]?.[dayIndex]
                                      ?.defaultMinStay,
                                  );
                                  const cellPromotions = getCellPromotions(
                                    room.rawRoomId,
                                    date,
                                    minimumStay,
                                  );
                                  const firstPromotion = cellPromotions[0];

                                  return (
                                    <td
                                      key={dateStr}
                                      data-testid={`night-${dateStr}`}
                                      onClick={() =>
                                        toggleSelectCell(room.id, dateStr)
                                      }
                                      className={[
                                        'py-1.5 px-2 text-center border-r border-gray-200 cursor-pointer text-xs',
                                        isSelected ? 'bg-orange-50/60' : '',
                                      ].join(' ')}
                                    >
                                      {firstPromotion && !isLoading ? (
                                        <button
                                          type="button"
                                          onClick={(event) => {
                                            // Don't also toggle the cell selection.
                                            event.stopPropagation();
                                            setSupplementsModal({
                                              dateStr,
                                              roomName: room.name,
                                              roomRawId: room.rawRoomId,
                                              minimumStay,
                                            });
                                          }}
                                          className="inline-flex max-w-[100px] items-center gap-1 rounded-full border border-purple-200 bg-purple-100 px-2 py-0.5 transition-colors hover:bg-purple-200"
                                        >
                                          <span className="min-w-0 truncate text-[10px] font-medium text-purple-700">
                                            {firstPromotion.name}
                                          </span>
                                          {cellPromotions.length > 1 && (
                                            <span className="shrink-0 text-[10px] font-semibold text-purple-500">
                                              +{cellPromotions.length - 1}
                                            </span>
                                          )}
                                        </button>
                                      ) : (
                                        <span className="text-gray-400">
                                          &mdash;
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            </React.Fragment>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={daysList.length + 1}
                              className="py-16 text-center text-gray-500"
                            >
                              <i className="fa-light fa-moon text-3xl text-gray-400 mb-2 block" />
                              <p className="text-sm font-medium">
                                {t('nightRatesEditor.placeholder')}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Floating / Sticky Bulk Action Bar when nights are selected */}
              {selectedKeys.length > 0 && (
                <div className="mt-4 p-3.5 bg-orange-50/90 border border-orange-300 rounded-lg shadow-sm flex flex-col md:flex-row items-center justify-between gap-4 transition-all">
                  <div className="flex items-center space-x-2 text-xs font-semibold text-orange-900">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-white text-[11px]">
                      <i className="fal fa-check" />
                    </div>
                    <span>{selectedSummary}</span>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        {t('nightRatesEditor.ratePerNight')}
                      </label>
                      <div className="w-28">
                        <KoobInputNumber
                          name="bulkRate"
                          size="sm"
                          min={0}
                          value={values.bulkRate}
                          onValueChange={(value) =>
                            setFieldValue('bulkRate', value ?? '')
                          }
                          iconRight={
                            <span className="text-xs">{currency}</span>
                          }
                        />
                      </div>
                    </div>

                    {selectionHasGroupPrices && (
                      <div className="flex items-center space-x-1.5">
                        <label className="text-xs font-medium text-gray-700">
                          {t('nightRatesEditor.groupRatePerNight')}
                        </label>
                        <div className="w-28">
                          <KoobInputNumber
                            name="bulkGitRate"
                            size="sm"
                            min={0}
                            value={values.bulkGitRate}
                            onValueChange={(value) =>
                              setFieldValue('bulkGitRate', value ?? '')
                            }
                            iconRight={
                              <span className="text-xs">{currency}</span>
                            }
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        {t('nightRatesEditor.minStay')}
                      </label>
                      <div className="w-16">
                        <KoobInputNumber
                          name="bulkMinStay"
                          size="sm"
                          min={1}
                          integer
                          value={values.bulkMinStay}
                          onValueChange={(value) =>
                            setFieldValue('bulkMinStay', value ?? '')
                          }
                        />
                      </div>
                    </div>

                    {allOrganizations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setTargetOrgsModalOpen(true)}
                        data-testid="apply-to-organizations"
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-200"
                      >
                        <i className="far fa-users" />
                        {t('nightRatesEditor.applyToOrganizations')}
                        <span className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          {t('nightRatesEditor.organizationsSelected', {
                            count: pickerSelection.length,
                          })}
                        </span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => clearSelection()}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      <i className="far fa-xmark" />
                      {t('nightRatesEditor.clear')}
                    </button>

                    <button
                      type="button"
                      data-testid="apply-to-selected"
                      onClick={() => handleApplyBulk(values, setFieldValue)}
                      className="inline-flex items-center gap-1.5 border-none rounded-md bg-orange-500 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-1 active:scale-[.98]"
                    >
                      <i className="far fa-check" />
                      {t('nightRatesEditor.applyToSelected')}
                    </button>
                  </div>
                </div>
              )}

              {/* Legend Footer */}
              <div className="mt-5 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-start text-xs text-gray-500 gap-x-6 gap-y-2">
                <div className="flex items-center space-x-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
                  <span>{t('nightRatesEditor.legendOverridden')}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-purple-500" />
                  <span>{t('nightRatesEditor.legendSupplement')}</span>
                </div>

                {hasSeasons && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`h-2.5 w-4 rounded-sm ${STATUS_CELL_CLASSES[NightStatusEnum.CLOSED]}`}
                      />
                      <span>{t('nightRatesEditor.legendClosed')}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span
                        className={`h-2.5 w-4 rounded-sm border border-gray-200 ${STATUS_CELL_CLASSES[NightStatusEnum.NOT_OFFERED]}`}
                      />
                      <span>{t('nightRatesEditor.legendNotOffered')}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Footer Bottom Actions: Reset & Save */}
              <div className="mt-4 flex justify-between items-center">
                <AllotmentButton
                  onClick={() => {
                    resetForm();
                    clearSelection();
                  }}
                  disabled={isSubmitting}
                  type="button"
                >
                  <i className="far fa-refresh mr-1.5" />
                  {t('misc:reset')}
                </AllotmentButton>

                <AllotmentButton
                  type="submit"
                  variant="primary"
                  disabled={
                    isSubmitting ||
                    !canUpdateHotel ||
                    dirtyRef.current === false
                  }
                  testId="submit-night-rates"
                >
                  {t('misc:save')}
                  <i className="far fa-save ml-1.5" />
                </AllotmentButton>
              </div>
            </Form>
          );
        }}
      </Formik>

      <NightRatesTargetOrganizationsModal
        open={targetOrgsModalOpen}
        onClose={() => setTargetOrgsModalOpen(false)}
        organizations={allOrganizations}
        selectedOrganizationIds={pickerSelection}
        setSelectedOrganizationIds={setPickerSelection}
        nightsCount={
          new Set(selectedKeys.map((k) => k.slice(k.lastIndexOf('_') + 1))).size
        }
      />

      {/* Supplements detail modal — lists the mandatory supplements applied to
          the clicked night, replacing the former hover tooltip. */}
      <KoobModal
        open={Boolean(supplementsModal)}
        setOpen={() => setSupplementsModal(null)}
        widthClass="sm:max-w-md"
      >
        <div className="p-5">
          <KoobTitle>{t('nightRatesEditor.supplementsModalTitle')}</KoobTitle>

          {supplementsModal && (
            <KoobParagraph>
              {t('nightRatesEditor.supplementsModalSubtitle', {
                room: supplementsModal.roomName,
                date: formatDate(supplementsModal.dateStr, 'D MMM YYYY'),
              })}
            </KoobParagraph>
          )}

          <div className="mt-5 flex flex-col gap-2">
            {modalPromotions.length > 0 ? (
              modalPromotions.map((promotion) => {
                const isPercent =
                  promotion.benefit?.kind === 'Benefits::SupplementPercent';
                const value = promotion.benefit?.value;
                const valueLabel =
                  value == null
                    ? ''
                    : isPercent
                      ? `+${value}%`
                      : `+${value} ${currency ?? ''}`.trim();

                return (
                  <div
                    key={promotion.id}
                    className="flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2"
                  >
                    <span className="h-2 w-2 shrink-0 rounded-full bg-purple-500" />
                    <span className="text-sm font-medium text-purple-800">
                      {promotion.name}
                    </span>
                    {valueLabel && (
                      <span className="ml-auto shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
                        {valueLabel}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <span className="text-sm text-gray-400">
                {t('nightRatesEditor.noSupplements')}
              </span>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <KoobButton
              variant="secondary"
              onClick={() => setSupplementsModal(null)}
            >
              {t('misc:close')}
            </KoobButton>
          </div>
        </div>
      </KoobModal>
    </KoobCard>
  );
}
